import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Tooltip,
  Popover,
  Row,
  Col,
  Space,
  Table,
  Tag,
  Popconfirm,
  message,
  Modal,
  InputNumber,
  Divider,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { 
  createFees, 
  getFees, 
  deleteFee 
} from '@/services/feeService';
import { 
  FeeSchema, 
  CreateFeeRequest, 
  FeeType, 
  FeeBandRequestSchema,
  DisburseType,
  TransactionPurpose,
  FlowType,
  FeeProvider,
} from '@/types/fees/fee-types';

const { Option } = Select;
const { Text } = Typography;

const CURRENCY_OPTIONS = ['USD', 'NGN', 'EUR', 'GBP'];
const DISBURSE_TYPE_OPTIONS: DisburseType[] = [
  'cash',
  'bank-account',
  'virtual-card',
  'physical-card',
];
const TRANSACTION_PURPOSE_OPTIONS: TransactionPurpose[] = [
  'exchange',
  'transfer',
  'card-creation',
  'card-transaction',
  'atm-withdrawal',
  'collection-fee',
  'payout-fee',
];
const FLOW_TYPE_OPTIONS: FlowType[] = [
  'ngn-fx-dom',
  'ngn-fx-offshore',
  'fx-ngn-offshore',
  'fx-dom-ngn',
];
const PROVIDER_OPTIONS: FeeProvider[] = ['flutterwave', 'nuvion', 'offline'];

interface FeeManagementFormProps {
  onSave?: () => void;
}

const renderFormLabel = (label: string, tooltip: string) => (
  <Space size={4}>
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 13, cursor: 'help' }} />
    </Tooltip>
  </Space>
);

const FeeManagementForm: React.FC<FeeManagementFormProps> = ({ onSave }) => {
  const [form] = Form.useForm();
  const [fees, setFees] = useState<FeeSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bands, setBands] = useState<FeeBandRequestSchema[]>([]);
  const [feeFiltersForm] = Form.useForm();
  const [feeFilters, setFeeFilters] = useState<{
    source_currency_code?: string;
    dest_currency_code?: string;
    fee_currency_code?: string;
    disburse_type?: DisburseType;
    transaction_purpose?: TransactionPurpose;
    vat?: string | number;
    flow?: FlowType;
    collection_provider?: FeeProvider;
    payout_provider?: FeeProvider;
    user_id?: string;
    profile_id?: string;
  }>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Calculate fee on: required, default to Source amount (applies_to_dest = false)
  // Switched to Form field instead of local state
  
  // Load existing fees
  useEffect(() => {
    loadFees();
  }, []);

  const loadFees = async (reset: boolean = true) => {
    try {
      setLoading(true);
      const params = { ...feeFilters } as any;
      const response = await getFees(params);
      setFees(response.fees || []);
      setNextCursor(response.next_cursor);
    } catch (error) {
      message.error('Failed to load fees');
      console.error('Error loading fees:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFees = async () => {
    if (!nextCursor) return;
    try {
      setIsFetchingMore(true);
      const params = { ...feeFilters, next_cursor: nextCursor } as any;
      const response = await getFees(params);
      setFees((prev) => ([...(prev || []), ...(response.fees || [])]));
      setNextCursor(response.next_cursor);
    } catch (error) {
      message.error('Failed to load more fees');
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      const feeData: CreateFeeRequest = {
        type: values.type,
        value: values.value,
        fee_currency_code: values.fee_currency_code,
        dest_currency_code: values.dest_currency_code,
        source_currency_code: values.source_currency_code,
        disburse_type: values.disburse_type,
        transaction_purpose: values.transaction_purpose,
        vat: values.vat,
        flow: values.flow,
        collection_provider: values.collection_provider,
        payout_provider: values.payout_provider,
        floor: values.floor,
        cap: values.cap,
        bands: bands.length > 0 ? bands : undefined,
        applies_to_dest: values.applies_to_dest,
      };

      await createFees([feeData]);
      message.success('Fee created successfully!');
      form.resetFields();
      setBands([]);
      loadFees();
      onSave?.();
    } catch (error) {
      message.error('Failed to create fee');
      console.error('Error creating fee:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyFeeFilters = async () => {
    const values = await feeFiltersForm.validateFields().catch(() => undefined);
    const clean = Object.entries(values || {}).reduce((acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== '') acc[k] = v as any;
      return acc;
    }, {} as any);
    setFeeFilters(clean);
  };

  useEffect(() => {
    // reload when feeFilters change
    loadFees(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeFilters]);

  const handleDelete = async (id: string) => {
    try {
      await deleteFee(id);
      message.success('Fee deleted successfully!');
      loadFees();
    } catch (error) {
      message.error('Failed to delete fee');
      console.error('Error deleting fee:', error);
    }
  };

  const addBand = () => {
    setBands([...bands, { minimum_amount: 0, maximum_amount: 0, fee_value: 0, fee_type: 'percentage' }]);
  };

  const updateBand = (index: number, field: keyof FeeBandRequestSchema, value: any) => {
    const newBands = [...bands];
    newBands[index] = { ...newBands[index], [field]: value };
    setBands(newBands);
  };

  const removeBand = (index: number) => {
    setBands(bands.filter((_, i) => i !== index));
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: FeeType) => (
        <Tag color={type === 'percentage' ? 'blue' : 'green'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: number, record: FeeSchema) => (
        <Text strong>
          {record.type === 'percentage'
            ? `${value}%`
            : `${value} ${record.fee_currency_code || record.dest_currency_code || ''}`}
        </Text>
      ),
    },
    {
      title: 'Currencies',
      key: 'currency',
      render: (record: FeeSchema) => (
        <div>
          <Text strong>{record.source_currency_code || '-'} → {record.dest_currency_code || '-'}</Text>
          <br />
          <Text type="secondary">Fee currency: {record.fee_currency_code || '-'}</Text>
        </div>
      ),
    },
    {
      title: 'Routing',
      key: 'routing',
      render: (record: FeeSchema) => {
        const hasRouting =
          record.disburse_type ||
          record.transaction_purpose ||
          record.flow ||
          record.collection_provider ||
          record.payout_provider;
        const hasVat =
          record.vat !== undefined && record.vat !== null && (record.vat as unknown) !== '';
        return (
          <Space size={[0, 4]} wrap>
            {record.disburse_type && <Tag>{record.disburse_type}</Tag>}
            {record.transaction_purpose && <Tag color="blue">{record.transaction_purpose}</Tag>}
            {record.flow && <Tag color="purple">{record.flow}</Tag>}
            {record.collection_provider && <Tag color="cyan">{record.collection_provider}</Tag>}
            {record.payout_provider && <Tag color="green">{record.payout_provider}</Tag>}
            {hasVat && <Tag color="gold">vat: {String(record.vat)}</Tag>}
            {!hasRouting && (
              <Tooltip title="No routing filters set — this fee applies to all transactions for the currency pair above.">
                <Tag color="default" className="fee-routing-empty">Applies to all</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (record: FeeSchema) => (
        <div>
          {record.floor && <Text type="secondary">Min: {record.floor}</Text>}
          {record.cap && <Text type="secondary">Max: {record.cap}</Text>}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: FeeSchema) => (
        <Space>
          {/* Update endpoint not defined in doc; hide edit for now */}
          <Popconfirm
            title="Are you sure you want to delete this fee?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fee-management">
      <div className="fee-hero">
        <div className="fee-hero__icon">
          <DollarOutlined />
        </div>
        <div>
          <p className="fee-hero__title">Fee Management System</p>
          <p className="fee-hero__desc">
            Create and manage platform fees with flexible qualification rules, tiered pricing, and currency-specific settings.
          </p>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Create Fee Form */}
        <Col span={24}>
          <Card
            className="fee-card"
            title={
              <Space>
                <span>Create New Fee</span>
                <Popover
                  placement="right"
                  trigger="click"
                  content={(
                    <div style={{ maxWidth: 360 }}>
                      <Typography.Paragraph strong>Examples</Typography.Paragraph>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        <li>
                          Percentage fee: Type = percentage, Value = 2 (2% of amount)
                        </li>
                        <li>
                          Flat fee: Type = flat, Value = 500 (fixed 500)
                        </li>
                        <li>
                          Fee currency is required for both flat and percentage fees
                        </li>
                        <li>
                          Currency-specific: Source = USD, Destination = NGN
                        </li>
                        <li>
                          Add transaction purpose, flow, and provider when the fee is route-specific
                        </li>
                      </ul>
                    </div>
                  )}
                >
                  <Button size="small" type="link">View examples</Button>
                </Popover>
              </Space>
            }
            size="small"
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                type: 'percentage',
                applies_to_dest: false,
              }}
            >
              <div className="fee-section">
                <div className="fee-section__title">Fee basics</div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Fee Type', 'How the fee is calculated: percentage or flat.')}
                      name="type"
                      rules={[{ required: true, message: 'Please select fee type' }]}
                    >
                      <Select>
                        <Option value="percentage">Percentage</Option>
                        <Option value="flat">Flat Amount</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Fee Value', 'The number used to compute the fee. For example, 2 means 2% and 500 means a flat charge of 500 in the selected fee currency.')}
                      name="value"
                      rules={[{ required: true, message: 'Please enter fee value' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                        placeholder="Enter fee value"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Calculate fee on', 'Choose whether the fee should be calculated on the source amount or the destination amount.')}
                      name="applies_to_dest"
                      rules={[{ required: true, message: 'Please select which amount to calculate on' }]}
                    >
                      <Select>
                        <Option value={true}>Destination amount</Option>
                        <Option value={false}>Source amount</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Fee Currency', 'Currency code the fee is being charged in. This is required for all fee entries.')}
                      name="fee_currency_code"
                      rules={[{ required: true, message: 'Please select the fee currency' }]}
                    >
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className="fee-section">
                <div className="fee-section__title">Currencies &amp; routing</div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Source Currency', 'Currency code the money is coming from. This is required for the fee setup.')}
                      name="source_currency_code"
                      rules={[{ required: true, message: 'Please select the source currency' }]}
                    >
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Destination Currency', 'Currency code the money is going to. This is required for the fee setup.')}
                      name="dest_currency_code"
                      rules={[{ required: true, message: 'Please select the destination currency' }]}
                    >
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Disburse Type', 'Optional. Use this when the fee should only apply to a specific disbursement type.')}
                      name="disburse_type"
                    >
                      <Select allowClear>
                        {DISBURSE_TYPE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Transaction Purpose', 'Optional. Limit this fee to a specific transaction purpose when needed.')}
                      name="transaction_purpose"
                    >
                      <Select allowClear showSearch>
                        {TRANSACTION_PURPOSE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Flow', 'Optional. Use this when the fee applies only to a specific flow type.')}
                      name="flow"
                    >
                      <Select allowClear showSearch>
                        {FLOW_TYPE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Collection Provider', 'Optional. Use this when the fee should only apply to a specific collection provider.')}
                      name="collection_provider"
                    >
                      <Select allowClear showSearch>
                        {PROVIDER_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Payout Provider', 'Optional. Use this when the fee should only apply to a specific payout provider.')}
                      name="payout_provider"
                    >
                      <Select allowClear showSearch>
                        {PROVIDER_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('VAT', 'Optional VAT percentage for this fee. Enter a numeric percent such as 7.5.')}
                      name="vat"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={100}
                        step={0.1}
                        placeholder="e.g. 7.5"
                        addonAfter="%"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className="fee-section">
                <div className="fee-section__title">Limits</div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Minimum Fee (Floor)', 'Optional lower limit for the fee amount. The fee will not go below this value.')}
                      name="floor"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                        placeholder="Optional minimum fee"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={renderFormLabel('Maximum Fee (Cap)', 'Optional upper limit for the fee amount. The fee will not go above this value.')}
                      name="cap"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                        placeholder="Optional maximum fee"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Fee Bands Section */}
              <Divider orientation="left">Tiered Pricing (Optional)</Divider>
              
              <div style={{ marginBottom: 16 }}>
                <Button 
                  type="dashed" 
                  icon={<PlusOutlined />} 
                  onClick={addBand}
                  style={{ width: '100%' }}
                >
                  Add Fee Band
                </Button>
              </div>

              {bands.map((band, index) => (
                <Card key={index} className="fee-band-card" size="small" style={{ marginBottom: 8 }}>
                  <Row gutter={[8, 8]} align="middle">
                    <Col span={4}>
                      <Text strong>Min Amount:</Text>
                      <InputNumber
                        value={band.minimum_amount}
                        onChange={(value) => updateBand(index, 'minimum_amount', value)}
                        style={{ width: '100%' }}
                        min={0}
                      />
                    </Col>
                    <Col span={4}>
                      <Text strong>Max Amount:</Text>
                      <InputNumber
                        value={band.maximum_amount}
                        onChange={(value) => updateBand(index, 'maximum_amount', value)}
                        style={{ width: '100%' }}
                        min={0}
                      />
                    </Col>
                    <Col span={4}>
                      <Text strong>Fee Value:</Text>
                      <InputNumber
                        value={band.fee_value}
                        onChange={(value) => updateBand(index, 'fee_value', value)}
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                      />
                    </Col>
                    <Col span={4}>
                      <Text strong>Fee Type:</Text>
                      <Select
                        value={band.fee_type}
                        onChange={(value) => updateBand(index, 'fee_type', value)}
                        style={{ width: '100%' }}
                      >
                        <Option value="percentage">Percentage</Option>
                        <Option value="flat">Flat</Option>
                      </Select>
                    </Col>
                    <Col span={4}>
                      <Button 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => removeBand(index)}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}

              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Space size={12}>
                  <Button
                    className="fee-submit-btn"
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={handleSave}
                    loading={saving}
                  >
                    Create Fee
                  </Button>
                  <Button
                    className="fee-reset-btn"
                    icon={<SettingOutlined />}
                    onClick={() => form.resetFields()}
                  >
                    Reset Form
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Col>

        {/* Fee Filters */}
        <Col span={24}>
          <Card className="fee-card" title="Fees" size="small" extra={(
            <Space>
              <Button onClick={() => { feeFiltersForm.resetFields(); setFeeFilters({}); }}>Clear</Button>
              <Button type="primary" onClick={handleApplyFeeFilters}>Apply Filters</Button>
            </Space>
          )}>
            <div className="fee-filters-panel">
              <div className="fee-filters-panel__header">
                <Text strong>filter fee entries</Text>
                <Text type="secondary">narrow the list by currency, routing, provider, or profile details.</Text>
              </div>

              <Form form={feeFiltersForm} layout="vertical" className="fee-filters-form">
                <Row gutter={[16, 6]}>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Fee Currency" name="fee_currency_code">
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Source Currency" name="source_currency_code">
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Destination Currency" name="dest_currency_code">
                      <Select allowClear>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <Option key={currency} value={currency}>{currency}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Disburse Type" name="disburse_type">
                      <Select allowClear>
                        {DISBURSE_TYPE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Transaction Purpose" name="transaction_purpose">
                      <Select allowClear showSearch>
                        {TRANSACTION_PURPOSE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="VAT" name="vat">
                      <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} placeholder="7.5" addonAfter="%" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Flow" name="flow">
                      <Select allowClear showSearch>
                        {FLOW_TYPE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Collection Provider" name="collection_provider">
                      <Select allowClear showSearch>
                        {PROVIDER_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Payout Provider" name="payout_provider">
                      <Select allowClear showSearch>
                        {PROVIDER_OPTIONS.map((option) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="User ID" name="user_id">
                      <Input placeholder="user_id" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8} xl={6}>
                    <Form.Item label="Profile ID" name="profile_id">
                      <Input placeholder="profile_id" allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
            <Table
              columns={columns}
              dataSource={fees}
              loading={loading}
              rowKey="id"
              pagination={false}
            />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              {nextCursor ? (
                <Button onClick={loadMoreFees} loading={isFetchingMore}>Load more</Button>
              ) : (
                <Text type="secondary">End of list</Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FeeManagementForm;
